/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable jsx-a11y/no-static-element-interactions */
import { headerMenuAtom } from "@app/shared/atoms";
import { useFeatureFlag } from "@app/shared/hooks/use-feature-flag";
import { URLS } from "@core/shared/utils";
import { CloseIcon } from "@design-system/components/Icons";
import { cn } from "@design-system/lib/utils";
import { trans, plainTrans } from "@mongez/localization";
import { useLocation } from "@remix-run/react";
import { useEffect } from "react";
import { BaseLink } from "../../../../design-system/components/link";
import { useNavigationLinks } from "../../../hooks/use-navigation-links";
import { EidAdhaDecoration } from "./EidAdhaDecoration";
import { EidDecoration } from "./EidDecoration";
import { HeaderHomeSwitcher } from "./HeaderHomeSwitcher";
import { HeaderLogo } from "./HeaderLogo";

export function NavbarDrawer() {
  const opened = headerMenuAtom.useOpened();
  const location = useLocation();
  const navigationLinks = useNavigationLinks();
  const isLeaderBoardFeatureEnabled = useFeatureFlag("leaderboard");
  const isEidAdhaEnabled = useFeatureFlag("eidAdha");
  const isEidFetrEnabled = useFeatureFlag("eidFetr");

  useEffect(() => {
    if (!opened) {
      document.body.style.overflow = "auto";
    } else {
      document.body.style.overflow = "hidden";
    }

    return () => {
      document.body.style.overflow = "auto";
    };
  }, [opened]);

  return (
    <>
      {/* Backdrop */}
      <div
        onClick={headerMenuAtom.toggle}
        role="button"
        tabIndex={0}
        className={
          "fixed inset-0 z-50 h-full w-full transform overflow-hidden bg-gray-900 bg-opacity-25 ease-in-out lg:hidden " +
          (opened
            ? "translate-x-0 transition-opacity duration-500"
            : "translate-x-full transition-all delay-500")
        }
      ></div>

      {/* Drawer */}
      <section
        className={
          "delay-400 fixed top-0 z-50 h-full w-screen max-w-lg bg-white shadow-xl transition-all duration-500 ease-in-out dark:bg-gray-900 lg:hidden" +
          (opened
            ? " pointer-events-auto ltr:!-translate-x-0 rtl:translate-x-0"
            : " pointer-events-none -translate-x-full rtl:translate-x-full")
        }
      >
        {/* Festive top strip — swaps with the active season. Adha
            takes priority if both are somehow on. */}
        {isEidAdhaEnabled ? (
          <EidAdhaDecoration />
        ) : isEidFetrEnabled ? (
          <EidDecoration />
        ) : null}

        <div className="relative flex h-full w-screen max-w-lg flex-col overflow-y-scroll px-4">
          <header className="text-lg font-bold">
            <div className="flex items-center justify-between py-4">
              <HeaderLogo />
              <button
                onClick={headerMenuAtom.close}
                title={plainTrans("close")}
                className="rounded-full p-2 transition-colors duration-200 hover:bg-gray-100 dark:hover:bg-gray-800"
              >
                <CloseIcon />
              </button>
            </div>
          </header>
          <hr className="h-[1px] w-full bg-gray-200 dark:bg-gray-700" />

          {/* Navigation Links */}
          <nav className="flex-1 py-6">
            <HeaderHomeSwitcher mobile onNavigate={headerMenuAtom.close} />
            <div className="space-y-2">
              {navigationLinks.map((link) => {
                const Icon = link.icon;
                const isActive = location.pathname.startsWith(link.to);

                if (
                  link.to === URLS.leaderboard.topAssessments &&
                  !isLeaderBoardFeatureEnabled
                ) {
                  return null;
                }

                const isLeaderboard =
                  link.to === URLS.leaderboard.topAssessments;
                const isChallenges = link.to === URLS.challenges.list;
                const isTrainingGround = link.to === URLS.trainingGround;
                const isAccent =
                  isLeaderboard || isChallenges || isTrainingGround;

                return (
                  <BaseLink
                    key={link.to}
                    to={link.to}
                    className={cn(
                      "group relative flex items-center gap-3 overflow-hidden rounded-lg px-4 py-3 text-base font-medium transition-all duration-300",
                      isLeaderboard &&
                        "border border-yellow-200/60 bg-gradient-to-r from-yellow-50 to-orange-50 dark:border-yellow-700/50 dark:from-yellow-900/20 dark:to-orange-900/20",
                      isChallenges &&
                        "border border-fuchsia-200/60 bg-gradient-to-r from-fuchsia-50 to-violet-50 dark:border-fuchsia-700/50 dark:from-fuchsia-900/20 dark:to-violet-900/20",
                      isTrainingGround &&
                        "border border-cyan-200/60 bg-gradient-to-r from-cyan-50 to-emerald-50 dark:border-cyan-700/50 dark:from-cyan-900/20 dark:to-emerald-900/20",
                      isActive && !isAccent
                        ? "border-l-4 border-primary bg-primary/10 text-primary"
                        : isActive && isLeaderboard
                          ? "bg-gradient-to-r from-yellow-100 to-orange-100 text-yellow-700 shadow-md dark:from-yellow-800/30 dark:to-orange-800/30 dark:text-yellow-300"
                          : isActive && isChallenges
                            ? "bg-gradient-to-r from-fuchsia-100 to-violet-100 text-fuchsia-700 shadow-md dark:from-fuchsia-800/30 dark:to-violet-800/30 dark:text-fuchsia-300"
                            : isActive && isTrainingGround
                              ? "bg-gradient-to-r from-cyan-100 to-emerald-100 text-cyan-700 shadow-md dark:from-cyan-800/30 dark:to-emerald-800/30 dark:text-cyan-300"
                              : !isAccent
                                ? "text-gray-700 hover:bg-gray-50 hover:shadow-sm dark:text-gray-300 dark:hover:bg-gray-800 dark:hover:text-gray-100"
                                : isLeaderboard
                                  ? "text-yellow-600 hover:scale-[1.02] hover:bg-gradient-to-r hover:from-yellow-100 hover:to-orange-100 hover:text-yellow-700 hover:shadow-md dark:text-yellow-400 dark:hover:from-yellow-800/30 dark:hover:to-orange-800/30 dark:hover:text-yellow-300"
                                  : isTrainingGround
                                    ? "text-cyan-600 hover:scale-[1.02] hover:bg-gradient-to-r hover:from-cyan-100 hover:to-emerald-100 hover:text-cyan-700 hover:shadow-md dark:text-cyan-400 dark:hover:from-cyan-800/30 dark:hover:to-emerald-800/30 dark:hover:text-cyan-300"
                                    : "text-fuchsia-600 hover:scale-[1.02] hover:bg-gradient-to-r hover:from-fuchsia-100 hover:to-violet-100 hover:text-fuchsia-700 hover:shadow-md dark:text-fuchsia-400 dark:hover:from-fuchsia-800/30 dark:hover:to-violet-800/30 dark:hover:text-fuchsia-300",
                    )}
                    onClick={headerMenuAtom.close}
                  >
                    {isLeaderboard && (
                      <div className="absolute inset-0 rounded-lg bg-gradient-to-r from-yellow-400/10 to-orange-500/10 opacity-0 transition-opacity duration-300 group-hover:opacity-100" />
                    )}
                    {isChallenges && (
                      <div className="absolute inset-0 rounded-lg bg-gradient-to-r from-fuchsia-400/10 to-violet-500/10 opacity-0 transition-opacity duration-300 group-hover:opacity-100" />
                    )}
                    {isTrainingGround && (
                      <div className="absolute inset-0 rounded-lg bg-gradient-to-r from-cyan-400/10 to-emerald-500/10 opacity-0 transition-opacity duration-300 group-hover:opacity-100" />
                    )}

                    <Icon
                      className={cn(
                        "h-5 w-5 flex-shrink-0 transition-all duration-300",
                        isLeaderboard
                          ? "text-yellow-500 group-hover:scale-110 dark:text-yellow-400"
                          : isChallenges
                            ? "text-fuchsia-500 group-hover:scale-110 dark:text-fuchsia-400"
                            : isTrainingGround
                              ? "text-cyan-500 group-hover:scale-110 dark:text-cyan-400"
                              : isActive
                                ? "text-primary"
                                : "text-gray-500 dark:text-gray-400",
                        isActive && isAccent && "animate-pulse",
                      )}
                    />

                    <span className="relative z-10 flex-1">{link.label}</span>

                    {isActive && !isAccent && (
                      <div className="h-2 w-2 rounded-full bg-primary" />
                    )}

                    {isLeaderboard && (
                      <span className="relative z-10 rounded-full bg-gradient-to-r from-yellow-400 to-orange-500 px-2 py-0.5 text-[10px] font-bold text-white shadow-sm">
                        🏆
                      </span>
                    )}

                    {isChallenges && (
                      <span className="relative z-10 rounded-full bg-gradient-to-r from-fuchsia-500 to-violet-500 px-2 py-0.5 text-[10px] font-bold text-white shadow-sm">
                        ⚔️
                      </span>
                    )}
                    {isTrainingGround && (
                      <span className="relative z-10 rounded-full bg-gradient-to-r from-cyan-500 to-emerald-500 px-2 py-0.5 text-[10px] font-bold text-white shadow-sm">
                        {trans("trainingGround.badge")}
                      </span>
                    )}

                    {isLeaderboard && (
                      <div className="absolute -right-0.5 -top-0.5 h-2.5 w-2.5 animate-pulse rounded-full bg-gradient-to-r from-yellow-400 to-orange-500 shadow-[0_0_6px_rgba(251,191,36,0.7)]" />
                    )}

                    {isChallenges && (
                      <div className="absolute -right-0.5 -top-0.5 h-2.5 w-2.5 animate-pulse rounded-full bg-gradient-to-r from-fuchsia-500 to-violet-500 shadow-[0_0_6px_rgba(217,70,239,0.7)]" />
                    )}
                    {isTrainingGround && (
                      <div className="absolute -right-0.5 -top-0.5 h-2.5 w-2.5 animate-pulse rounded-full bg-gradient-to-r from-cyan-400 to-emerald-500 shadow-[0_0_6px_rgba(20,184,166,0.7)]" />
                    )}
                  </BaseLink>
                );
              })}
            </div>
          </nav>
        </div>
      </section>
    </>
  );
}
